/**
 * Curated taxonomies for title family and field scoring.
 *
 * Day 2 uses a hand-written subset that covers the most common cases in
 * our demo. Day 3 expands with ESCO + O*NET imports (see
 * `packages/taxonomy-data/README.md`).
 *
 * Design:
 *   - A title family is a group of interchangeable role names. Two titles
 *     in the same family score 1.0; adjacent families score 0.7; unrelated
 *     families score 0.3.
 *   - A field is the broader domain (IT, Data, Health, Finance, etc.).
 *     Fields have a "transfer matrix" for cross-domain bonuses.
 */

// ---------------------------------------------------------------------------
// Title families
// ---------------------------------------------------------------------------
export const TITLE_FAMILIES: ReadonlyArray<{ id: string; titles: string[] }> = [
  {
    id: "software_backend",
    titles: [
      "software engineer", "backend engineer", "back end engineer", "back-end engineer",
      "senior software engineer", "staff software engineer", "principal engineer",
      "platform engineer", "systems engineer", "server engineer",
      "python engineer", "go engineer", "java engineer", "rust engineer",
      "api engineer", "infrastructure engineer", "cloud engineer",
    ],
  },
  {
    id: "software_frontend",
    titles: [
      "frontend engineer", "front end engineer", "front-end engineer", "ui engineer",
      "react engineer", "react developer", "web developer", "javascript developer",
      "typescript developer", "client engineer",
    ],
  },
  {
    id: "software_fullstack",
    titles: [
      "full stack engineer", "full-stack engineer", "fullstack engineer", "full stack developer",
    ],
  },
  {
    id: "software_mobile",
    titles: [
      "mobile engineer", "ios engineer", "android engineer", "mobile developer",
      "ios developer", "android developer", "react native developer",
    ],
  },
  {
    id: "software_devops",
    titles: [
      "devops engineer", "sre", "site reliability engineer", "platform engineer",
      "infrastructure engineer", "release engineer", "production engineer",
    ],
  },
  {
    id: "data_engineering",
    titles: [
      "data engineer", "senior data engineer", "analytics engineer",
      "etl developer", "big data engineer", "data platform engineer",
    ],
  },
  {
    id: "data_science",
    titles: [
      "data scientist", "senior data scientist", "research scientist",
      "applied scientist", "quantitative analyst", "quant researcher",
      "statistical analyst", "bi analyst", "business intelligence analyst",
    ],
  },
  {
    id: "ml_engineering",
    titles: [
      "machine learning engineer", "ml engineer", "ai engineer",
      "senior machine learning engineer", "senior ml engineer",
      "nlp engineer", "computer vision engineer", "mlops engineer",
      "prompt engineer", "deep learning engineer",
    ],
  },
  {
    id: "product_management",
    titles: [
      "product manager", "senior product manager", "group product manager",
      "principal product manager", "associate product manager", "apm",
      "director of product", "vp product", "chief product officer",
      "product lead", "product owner",
    ],
  },
  {
    id: "design",
    titles: [
      "product designer", "ux designer", "ui designer", "senior designer",
      "design lead", "staff designer", "visual designer",
    ],
  },
  {
    id: "clinical_research",
    titles: [
      "clinical researcher", "clinical research physician", "clinical fellow",
      "research scientist", "medical officer", "biostatistician", "epidemiologist",
      "clinical data scientist", "clinical informaticist",
    ],
  },
];

// ---------------------------------------------------------------------------
// Fields — broad domains
// ---------------------------------------------------------------------------
export const FIELDS = [
  "IT",
  "Data",
  "ML_AI",
  "Product",
  "Healthcare",
  "Finance",
  "Marketing",
  "Sales",
  "HR",
  "Legal",
  "Operations",
  "Education",
  "Research",
  "Design",
  "Other",
] as const;
export type Field = (typeof FIELDS)[number];

// Field keyword vocabulary. A document's field is assigned by counting
// keyword hits per field; the max wins (with a tie-break in declaration order).
export const FIELD_KEYWORDS: Record<Field, string[]> = {
  IT: [
    "software engineer", "backend engineer", "frontend engineer", "full stack",
    "python", "typescript", "javascript", "java", "rust", "golang",
    "api", "microservices", "kubernetes", "docker",
    "aws", "gcp", "azure", "postgres", "redis", "kafka",
    "devops", "sre", "ci/cd", "react", "next.js", "vue", "angular",
    "code review", "pull request", "git", "deployment",
  ],
  Data: [
    "data engineer", "etl", "data warehouse", "snowflake", "bigquery",
    "databricks", "airflow", "dbt", "data pipeline", "data quality", "data catalog",
    "tableau", "looker", "powerbi",
  ],
  ML_AI: [
    "machine learning", "deep learning", "neural network",
    "pytorch", "tensorflow", "scikit-learn", "xgboost", "lightgbm",
    "llm", "large language model", "rag", "embedding", "vector database",
    "fine-tuning", "prompt engineering", "recommendation system", "ranker",
    "feature store", "mlops", "model serving",
  ],
  Product: [
    "product manager", "product management", "product strategy", "roadmap",
    "product owner", "product lead", "prd", "user stories", "stakeholder management",
    "product requirements", "okr", "product-market fit", "go-to-market",
    "product discovery", "product metrics", "feature prioritization",
    "customer interviews", "product roadmap", "product vision",
  ],
  Healthcare: [
    "clinical", "patient", "medical", "hospital", "physician", "nurse",
    "fda", "irb", "cohort", "observational study", "trial",
    "pharmaceutical", "diagnosis", "treatment", "therapy", "biomarker",
    "ehr", "epic", "cerner", "hipaa", "nursing", "practitioner",
  ],
  Finance: [
    "finance", "banking", "trading", "equity", "bond", "portfolio",
    "risk", "compliance", "audit", "accounting", "gaap", "payroll",
    "fintech", "payments", "transaction", "credit", "fraud",
    "dcf", "lbo", "investment banking", "financial modeling",
  ],
  Marketing: [
    "marketing", "campaign", "brand", "seo", "sem", "advertising",
    "growth", "funnel", "email marketing", "content marketing", "social media",
    "attribution", "ctr", "cac", "ltv", "demand generation",
  ],
  Sales: [
    "sales", "quota", "pipeline", "crm", "salesforce",
    "account executive", "business development", "enterprise sales",
    "prospecting", "outbound", "inbound", "closing",
  ],
  HR: [
    "human resources", "recruiting", "talent acquisition", "onboarding",
    "benefits", "compensation", "hris", "workday", "bamboohr",
    "performance review", "employee engagement",
  ],
  Legal: [
    "legal", "attorney", "lawyer", "counsel", "litigation", "contract",
    "compliance", "compliance officer", "compliance program", "compliance policy",
    "regulation", "patent", "trademark",
    "regulatory", "bar admission", "brief", "deposition",
    "sec", "finra", "enforcement", "governance", "risk assessment",
  ],
  Operations: [
    "operations manager", "supply chain", "logistics", "warehouse",
    "fulfillment", "inventory", "procurement", "distribution",
    "lean six sigma", "continuous improvement", "throughput",
    "facility management", "fleet management", "osha",
  ],
  Education: [
    "teacher", "professor", "curriculum", "lesson plan", "pedagogy",
    "student", "lecture", "grading", "academic", "classroom",
  ],
  Research: [
    "research", "publication", "paper", "journal", "peer review",
    "experiment", "hypothesis", "laboratory", "nsf", "nih", "phd",
    "postdoc",
  ],
  Design: [
    "design", "figma", "sketch", "wireframe", "prototype", "user research",
    "usability", "interaction design", "visual design", "design system",
    "accessibility",
  ],
  Other: [],
};

// Cross-domain transfer bonuses. Rows = resume field, cols = JD field.
// A 1.0 means "perfect match or strongly transferable", a 0.3 means
// "unrelated with little transfer". Symmetric.
export const FIELD_TRANSFER: Record<string, Record<string, number>> = {
  IT: { IT: 1.0, Data: 0.75, ML_AI: 0.80, Product: 0.35, Design: 0.50, Other: 0.30 },
  Data: { IT: 0.75, Data: 1.0, ML_AI: 0.85, Research: 0.60, Finance: 0.65, Healthcare: 0.55, Other: 0.30 },
  ML_AI: { IT: 0.80, Data: 0.85, ML_AI: 1.0, Research: 0.70, Healthcare: 0.55, Other: 0.30 },
  Product: { Product: 1.0, IT: 0.35, Design: 0.55, Marketing: 0.50, Sales: 0.45, Other: 0.30 },
  Healthcare: { Healthcare: 1.0, Research: 0.70, Data: 0.55, ML_AI: 0.55, Other: 0.25 },
  Finance: { Finance: 1.0, Data: 0.65, ML_AI: 0.55, Legal: 0.65, Other: 0.30 },
  Marketing: { Marketing: 1.0, Sales: 0.55, Product: 0.50, Data: 0.40, Other: 0.25 },
  Sales: { Sales: 1.0, Marketing: 0.55, Product: 0.45, Other: 0.25 },
  HR: { HR: 1.0, Other: 0.25 },
  Legal: { Legal: 1.0, Finance: 0.65, HR: 0.45, Healthcare: 0.40, Other: 0.25 },
  Operations: { Operations: 1.0, IT: 0.35, Data: 0.45, Other: 0.30 },
  Education: { Education: 1.0, Research: 0.65, Other: 0.30 },
  Research: { Research: 1.0, Data: 0.60, ML_AI: 0.70, Healthcare: 0.70, Other: 0.35 },
  Design: { Design: 1.0, IT: 0.50, Product: 0.55, Marketing: 0.45, Other: 0.30 },
  Other: { Other: 1.0 },
};

/** Lookup transfer score for a pair of fields, defaulting to 0.3 on miss. */
export function fieldTransfer(resumeField: Field, jdField: Field): number {
  return FIELD_TRANSFER[resumeField]?.[jdField] ?? 0.3;
}

/** Assign a field to a document by keyword hit count. */
export function classifyField(text: string): { field: Field; confidence: number } {
  const lower = text.toLowerCase();
  let bestField: Field = "Other";
  let bestHits = 0;

  for (const field of FIELDS) {
    if (field === "Other") continue;
    let hits = 0;
    for (const kw of FIELD_KEYWORDS[field]) {
      if (lower.includes(kw)) hits += kw.includes(" ") ? 2 : 1;
    }
    if (hits > bestHits) {
      bestHits = hits;
      bestField = field;
    }
  }

  // Confidence is just hit density vs a small floor — good enough for Day 2.
  const confidence = bestHits === 0 ? 0.0 : Math.min(1.0, bestHits / 10);
  return { field: bestField, confidence };
}

/** Classify a title into a title-family id. Returns null on no match. */
export function classifyTitleFamily(title: string | null | undefined): string | null {
  if (!title) return null;
  const lower = title.toLowerCase();

  // Longest-match wins so "senior machine learning engineer" picks ml_engineering
  // before it could accidentally match software_backend on "engineer".
  let best: { id: string; length: number } | null = null;
  for (const family of TITLE_FAMILIES) {
    for (const candidate of family.titles) {
      if (lower.includes(candidate)) {
        if (!best || candidate.length > best.length) {
          best = { id: family.id, length: candidate.length };
        }
      }
    }
  }
  return best?.id ?? null;
}

/** Score two title-family ids against each other. */
export function scoreTitleFamilies(a: string | null, b: string | null): number {
  if (!a || !b) return 0.3;
  if (a === b) return 1.0;
  // Same "super-family" (software_*, data_*, ml_*) = 0.7
  const prefixA = a.split("_")[0];
  const prefixB = b.split("_")[0];
  if (prefixA && prefixA === prefixB) return 0.7;
  // ML overlaps heavily with data
  if ((a.startsWith("ml_") && b.startsWith("data_")) || (b.startsWith("ml_") && a.startsWith("data_"))) {
    return 0.7;
  }
  return 0.3;
}
